import { colors, el, btn } from "../ui/app";
import type { LocalGameState } from "../client/model/LocalGameState";
import type { ActionPromptMessage } from "../client/ws/OutboundMessage";
import type {
  InboundMessage,
  ChooseOffenseDefense,
  SubmitFormation,
  MoveAction,
  StandUpAction,
  TackleAction,
  ThrowAction,
  EndActivation,
  TakeKnee,
} from "../client/ws/InboundMessage";
import type { PlacementDto } from "../client/ws/MessageDtos";
import type { FieldLayout } from "./fieldRenderer";
import {
  parseActionPromptData,
  type LegalActions,
  type TackleOdds,
  type ThrowTarget,
} from "../client/ws/ActionPromptTypes";


export interface RenderState {
  highlights: Set<string>;
  moveRange: Set<string>;
  tackleHighlights: Set<string>;
  throwHighlights: Set<string>;
  placementPreview: Map<string, [number, number]>;
  movePath: Array<[number, number]>;
  movePathOrigin: [number, number] | null;
  previewLine: { from: [number, number]; to: [number, number] } | null;
  fieldClickHandler: ((gx: number, gy: number) => void) | null;
}

export interface PromptContext {
  state: LocalGameState;
  isHome: boolean;
  renderState: RenderState;
  getLayout: () => FieldLayout;
}

// ─── Panel container ─────────────────────────────────────────────────────────

export const PANEL_BOTTOM_HEIGHT = 300;

let panel: HTMLDivElement | null = null;
let eventLogEl: HTMLDivElement | null = null;
let overlayEl: HTMLDivElement | null = null;      // prompt layer  (z-index 10)
let hoverOverlayEl: HTMLDivElement | null = null; // hover layer   (z-index 20)

export function createGamePanel(): HTMLDivElement {
  panel = el("div", {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    height: `${PANEL_BOTTOM_HEIGHT}px`,
    background: "rgba(10, 10, 20, 0.97)",
    borderTop: `1px solid ${colors.border}`,
    display: "flex",
    justifyContent: "center",
    fontFamily: "'Courier New', Courier, monospace",
    color: colors.text,
    zIndex: "50",
    boxSizing: "border-box",
  });

  // Inner column — max-width matches the canvas cap (800px) so both center identically
  const inner = el("div", {
    width: "100%",
    maxWidth: "800px",
    height: "100%",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  });

  // Event log fills the inner column
  const logHeader = el("div", { padding: "8px 20px 6px", flexShrink: "0" });
  const logTitle = document.createElement("p");
  logTitle.textContent = "EVENT LOG";
  Object.assign(logTitle.style, {
    margin: "0",
    fontSize: "11px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    color: colors.muted,
  });
  logHeader.appendChild(logTitle);
  inner.appendChild(logHeader);

  eventLogEl = el("div", {
    flex: "1",
    overflowY: "auto",
    padding: "0 20px 10px",
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  });
  const emptyMsg = el("p", { margin: "0", fontSize: "12px", color: colors.muted, fontStyle: "italic" });
  emptyMsg.textContent = "Waiting for game events…";
  emptyMsg.dataset["emptyMsg"] = "1";
  eventLogEl.appendChild(emptyMsg);
  inner.appendChild(eventLogEl);

  // Prompt overlay — covers the event log when a prompt is active
  overlayEl = el("div", {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    background: "rgba(10, 10, 20, 0.97)",
    overflowY: "auto",
    display: "none",
    flexDirection: "column",
    zIndex: "10",
    boxSizing: "border-box",
  });
  inner.appendChild(overlayEl);

  // Hover overlay — sits above the prompt layer; shows player info on hover
  hoverOverlayEl = el("div", {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    background: "rgba(10, 10, 20, 0.97)",
    overflowY: "auto",
    display: "none",
    flexDirection: "column",
    zIndex: "20",
    boxSizing: "border-box",
    padding: "16px 20px",
  });
  inner.appendChild(hoverOverlayEl);

  panel.appendChild(inner);
  document.body.appendChild(panel);
  return panel;
}

export function getGamePanel(): HTMLDivElement | null {
  return panel;
}

/** Appends entries to the persistent event log and scrolls to the latest. */
export function appendToEventLog(entries: string[]): void {
  if (!eventLogEl || entries.length === 0) return;
  // Remove the "waiting" placeholder on first real entry
  const placeholder = eventLogEl.querySelector("[data-empty-msg]");
  if (placeholder) placeholder.remove();
  // Dim all existing entries once before appending
  for (const child of eventLogEl.children) {
    (child as HTMLElement).style.color = colors.muted;
  }
  for (const text of entries) {
    const line = el("p", { margin: "0", fontSize: "12px", color: colors.text });
    line.textContent = text;
    eventLogEl.appendChild(line);
    // Keep at most 100 entries
    while (eventLogEl.children.length > 100) eventLogEl.removeChild(eventLogEl.firstChild!);
  }
  eventLogEl.scrollTop = eventLogEl.scrollHeight;
}

function clearPanel(): void {
  if (!overlayEl) return;
  overlayEl.innerHTML = "";
  overlayEl.style.display = "none";
}

function setContent(node: HTMLElement): void {
  if (!overlayEl) return;
  overlayEl.innerHTML = "";
  overlayEl.appendChild(node);
  overlayEl.style.display = "flex";
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function actionTitle(text: string): HTMLHeadingElement {
  const h = document.createElement("h2");
  h.textContent = text;
  Object.assign(h.style, {
    margin: "0 0 4px 0",
    fontSize: "16px",
    color: colors.gold,
    letterSpacing: "1px",
  });
  return h;
}

function subtext(text: string): HTMLParagraphElement {
  const p = document.createElement("p");
  p.textContent = text;
  Object.assign(p.style, { margin: "0 0 14px 0", fontSize: "12px", color: colors.muted });
  return p;
}

function errorLine(): HTMLParagraphElement {
  const p = document.createElement("p");
  Object.assign(p.style, { margin: "6px 0 0 0", fontSize: "12px", color: colors.danger, minHeight: "16px" });
  return p;
}

// ─── CHOOSE_OFFENSE_DEFENSE ───────────────────────────────────────────────────

function showOffenseDefense(): Promise<ChooseOffenseDefense> {
  return new Promise((resolve) => {
    const wrap = el("div", { padding: "24px 20px" });
    wrap.appendChild(actionTitle("Coin Toss"));
    wrap.appendChild(subtext("You won! Choose your side."));

    const btnRow = el("div", { display: "flex", gap: "12px" });

    const oBtn = btn("OFFENSE", "primary");
    oBtn.style.flex = "1";
    oBtn.addEventListener("click", () => resolve({ type: "CHOOSE_OFFENSE_DEFENSE", choice: "OFFENSE" }));

    const dBtn = btn("DEFENSE", "secondary");
    dBtn.style.flex = "1";
    dBtn.addEventListener("click", () => resolve({ type: "CHOOSE_OFFENSE_DEFENSE", choice: "DEFENSE" }));

    btnRow.appendChild(oBtn);
    btnRow.appendChild(dBtn);
    wrap.appendChild(btnRow);
    setContent(wrap);
  });
}

// ─── SUBMIT_FORMATION ────────────────────────────────────────────────────────

function showFormation(
  prompt: ActionPromptMessage,
  ctx: PromptContext,
): Promise<SubmitFormation> {
  return new Promise((resolve) => {
    const { state, isHome, renderState } = ctx;
    const myPlayerIds = isHome ? state.homePlayerIds : state.awayPlayerIds;
    const prefix = isHome ? "H" : "A";
    const isOffense = isHome === state.offenseGoingNorth;
    const toPlace = state.playersPerDown;

    // Apply ball position from prompt data if present
    const bpData = prompt.data["ballPosition"] as Record<string, unknown> | undefined;
    if (bpData && typeof bpData.x === "number" && typeof bpData.y === "number") {
      state.ballPosition = [bpData.x, bpData.y];
    }

    const placements = new Map<string, [number, number]>(); // playerId → [gx, gy]
    let selectedPid: string | null = null;
    let ballCarrierId: string | null = null;
    renderState.placementPreview.clear();

    const wrap = el("div", { padding: "16px 20px", display: "flex", flexDirection: "column", gap: "10px", height: "100%", boxSizing: "border-box", overflowY: "auto" });
    wrap.appendChild(actionTitle("Set Formation"));
    const instrText = subtext(
      `Place ${toPlace} players. ${isOffense ? "You are on OFFENSE." : "You are on DEFENSE."} Click a player, then click the field to place them.`
    );
    wrap.appendChild(instrText);

    const err = errorLine();
    wrap.appendChild(err);

    // Roster list
    const rosterList = el("div", { display: "flex", flexDirection: "column", gap: "6px" });
    wrap.appendChild(rosterList);

    // Ball carrier section (shown after placement if offense)
    const bcSection = el("div", { display: "none" });
    const bcTitle = document.createElement("p");
    Object.assign(bcTitle.style, { margin: "0 0 8px 0", fontSize: "12px", color: colors.muted });
    bcTitle.textContent = "SELECT BALL CARRIER";
    bcSection.appendChild(bcTitle);
    const bcList = el("div", { display: "flex", flexDirection: "column", gap: "6px" });
    bcSection.appendChild(bcList);
    wrap.appendChild(bcSection);

    const submitBtn = btn("Submit Formation", "primary");
    submitBtn.style.display = "none";
    submitBtn.style.marginTop = "12px";
    wrap.appendChild(submitBtn);

    function makePlayerRow(pid: string, idx: number): { row: HTMLDivElement; statusEl: HTMLSpanElement } {
      const rowLabel = `${prefix}${idx + 1}`;
      const playerInfo = state.players.get(pid);
      const playerName = state.playerNames.get(pid) ?? rowLabel;
      const roleRaw = playerInfo?.role ?? "";
      // Strip allegiance prefix: "ELF_FODDER" → "FODDER"
      const roleName = roleRaw.includes("_") ? roleRaw.split("_").slice(1).join(" ") : roleRaw;

      const row = el("div", {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        padding: "8px 10px",
        borderRadius: "4px",
        border: `1px solid ${colors.border}`,
        background: "#0a0a14",
        cursor: "pointer",
      });

      // Top line: name + status
      const topLine = el("div", { display: "flex", justifyContent: "space-between", alignItems: "center" });

      const nameEl = document.createElement("span");
      nameEl.textContent = playerName;
      Object.assign(nameEl.style, { fontWeight: "bold", color: colors.gold, fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" });

      const statusEl = document.createElement("span");
      statusEl.textContent = "unplaced";
      Object.assign(statusEl.style, { fontSize: "11px", color: colors.muted, flexShrink: "0", marginLeft: "8px" });

      topLine.appendChild(nameEl);
      topLine.appendChild(statusEl);

      // Bottom line: role + stats
      const bottomLine = el("div", { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" });

      function chip(text: string, color: string): HTMLSpanElement {
        const s = document.createElement("span");
        s.textContent = text;
        Object.assign(s.style, { fontSize: "10px", color, letterSpacing: "0.5px" });
        return s;
      }

      if (roleName) bottomLine.appendChild(chip(roleName, colors.muted));

      if (playerInfo) {
        const sign = (n: number) => n >= 0 ? `+${n}` : `${n}`;
        bottomLine.appendChild(chip(`MOV ${playerInfo.mov}`, colors.text));
        bottomLine.appendChild(chip(`STR ${sign(playerInfo.str)}`, colors.text));
        bottomLine.appendChild(chip(`DEX ${sign(playerInfo.dex)}`, colors.text));
        for (const skill of playerInfo.skills) {
          const skillName = skill.replace(/_/g, " ");
          bottomLine.appendChild(chip(skillName, colors.accent));
        }
      }

      row.appendChild(topLine);
      row.appendChild(bottomLine);

      row.addEventListener("click", () => {
        if (placements.has(pid)) {
          // Unplace: remove and re-select for repositioning
          placements.delete(pid);
          renderState.placementPreview.delete(pid);
          if (ballCarrierId === pid) ballCarrierId = null;
          bcSection.style.display = "none";
          submitBtn.style.display = "none";
          renderState.fieldClickHandler = fieldClickHandler;
        }
        selectedPid = pid;
        document.querySelectorAll("[data-player-row]").forEach((el) => {
          (el as HTMLElement).style.borderColor = colors.border;
        });
        row.style.borderColor = colors.accent;
        refreshUI();
      });

      row.dataset["playerRow"] = pid;

      return { row, statusEl };
    }

    // Build roster — skip players with persistent injuries (can't play this down)
    const rows = new Map<string, { row: HTMLDivElement; status: HTMLSpanElement }>();
    myPlayerIds.forEach((pid, idx) => {
      const status = state.getPlayerStatus(pid);
      if (status === "KNOCKED_OUT" || status === "INCAPACITATED") return;
      const { row, statusEl } = makePlayerRow(pid, idx);
      rows.set(pid, { row, status: statusEl });
      rosterList.appendChild(row);
    });

    function refreshUI(): void {
      const placed = placements.size;
      // Update row statuses
      for (const [pid, { row, status }] of rows) {
        const pos = placements.get(pid);
        if (pos) {
          status.textContent = `(${pos[0]}, ${pos[1]})`;
          status.style.color = colors.success;
          row.style.borderColor = colors.success;
          row.style.cursor = "default";
        } else if (pid !== selectedPid) {
          status.textContent = "unplaced";
          status.style.color = colors.muted;
          row.style.borderColor = colors.border;
          row.style.cursor = "pointer";
        }
      }

      if (placed >= toPlace || placed >= rows.size) {
        // Done placing
        renderState.fieldClickHandler = null;
        if (isOffense && !ballCarrierId) {
          // Show ball carrier selection — only players in the same column and within 2 rows of the ball
          bcSection.style.display = "block";
          bcList.innerHTML = "";
          const [ballX, ballY] = state.ballPosition ?? [-1, -1];
          const eligiblePids = [...placements.entries()]
            .filter(([, [px, py]]) => px === ballX && Math.abs(py - ballY) <= 2)
            .map(([pid]) => pid);

          if (eligiblePids.length === 0) {
            const noEligibleMsg = document.createElement("p");
            noEligibleMsg.textContent = `No player is within 2 spaces of the ball (column ${ballX}, row ${ballY}). Place a player there before selecting a ball carrier.`;
            Object.assign(noEligibleMsg.style, { margin: "0", fontSize: "12px", color: colors.muted, fontStyle: "italic" });
            bcList.appendChild(noEligibleMsg);
          } else {
            for (const pid of eligiblePids) {
              const pos = placements.get(pid)!;
              const rowLabel = `${prefix}${myPlayerIds.indexOf(pid) + 1}`;
              const bcRow = el("div", {
                padding: "7px 10px",
                borderRadius: "4px",
                border: `1px solid ${colors.border}`,
                cursor: "pointer",
                fontSize: "13px",
              });
              const bcName = state.playerNames.get(pid) ?? rowLabel;
              bcRow.textContent = `${bcName} at (${pos[0]}, ${pos[1]})`;
              bcRow.addEventListener("click", () => {
                ballCarrierId = pid;
                document.querySelectorAll("[data-bc-row]").forEach((e) => {
                  (e as HTMLElement).style.borderColor = colors.border;
                });
                bcRow.style.borderColor = colors.gold;
                submitBtn.style.display = "block";
              });
              bcRow.setAttribute("data-bc-row", "");
              bcList.appendChild(bcRow);
            }
          }
        } else {
          submitBtn.style.display = "block";
        }
      }
    }

    // Field click → place selected player
    const fieldClickHandler = (gx: number, gy: number) => {
      if (!selectedPid) { err.textContent = "Select a player first."; return; }
      if (placements.has(selectedPid)) { err.textContent = "Already placed."; return; }
      if (placements.size >= toPlace) { err.textContent = `Already placed ${toPlace} players.`; return; }

      if (state.lineOfScrimmage !== null) {
        const dy = state.offenseGoingNorth ? 1 : -1;
        const los = state.lineOfScrimmage;
        if (isOffense && (gy - los) * dy > 0) {
          err.textContent = "Offense must place on or behind the line of scrimmage.";
          return;
        }
        if (!isOffense && (gy - los) * dy <= 0) {
          err.textContent = "Defense must place past the line of scrimmage.";
          return;
        }
      }

      const occupied = [...placements.values()].some(([px, py]) => px === gx && py === gy);
      if (occupied) { err.textContent = "A player is already placed there."; return; }

      err.textContent = "";
      placements.set(selectedPid, [gx, gy]);
      renderState.placementPreview.set(selectedPid, [gx, gy]);
      selectedPid = null;
      document.querySelectorAll("[data-player-row]").forEach((el) => {
        (el as HTMLElement).style.borderColor = colors.border;
      });
      refreshUI();
    };
    renderState.fieldClickHandler = fieldClickHandler;

    submitBtn.addEventListener("click", () => {
      if (isOffense && !ballCarrierId) { err.textContent = "Choose a ball carrier."; return; }
      const placementsList: PlacementDto[] = [];
      for (const [pid, [x, y]] of placements) {
        placementsList.push({ playerId: pid, x, y });
      }
      renderState.fieldClickHandler = null;
      // Keep placementPreview visible until FormationsRevealed clears it
      resolve({ type: "SUBMIT_FORMATION", placements: placementsList, ballCarrierId: ballCarrierId ?? null });
    });

    setContent(wrap);
  });
}

// ─── Path-finding for move submission ────────────────────────────────────────

/**
 * BFS through unoccupied in-bounds cells to find a path from start to goal.
 * Routes through any passable cell (not just the feed's reachable set) so that
 * intermediate cells which aren't legal stopping points don't break pathfinding.
 * Returns empty array if no path exists (caller should log and abort the move).
 */
function findPath(
  start: [number, number],
  goal: [number, number],
  moveTargets: MoveTarget[],
): Array<[number, number]> {
  const startKey = `${start[0]},${start[1]}`;
  const goalKey  = `${goal[0]},${goal[1]}`;

  // Build cost map: start has cost 0, every feed move target has its engine-computed cost.
  const costMap = new Map<string, number>([[startKey, 0]]);
  for (const t of moveTargets) costMap.set(`${t.x},${t.y}`, t.cost);

  if (!costMap.has(goalKey)) return [];

  // Backward greedy: trace from goal to start, always picking the adjacent reachable
  // cell with the highest cost strictly below the current cell's cost.
  // This is the engine's optimal predecessor at each step (maximising predecessor cost
  // = minimising marginal step cost = following the Dijkstra tree), so the reconstructed
  // path has total MOV cost equal to costMap[goal] and will be accepted by the server.
  const DIRS: [number, number][] = [
    [-1, 0], [1, 0], [0, -1], [0, 1],
    [-1, -1], [-1, 1], [1, -1], [1, 1],
  ];

  const path: Array<[number, number]> = [];
  let [cx, cy] = goal;
  let currentCost = costMap.get(goalKey)!;

  while (`${cx},${cy}` !== startKey) {
    let bestX = -1, bestY = -1, bestCost = -1, bestDist = Infinity;
    for (const [dx, dy] of DIRS) {
      const nx = cx + dx, ny = cy + dy;
      const nCost = costMap.get(`${nx},${ny}`);
      if (nCost === undefined || nCost >= currentCost) continue;
      // Primary: highest cost (smallest marginal step cost = engine's optimal predecessor).
      // Tiebreak: smallest Manhattan distance to start, keeping the path direct and
      // reducing the chance of routing through costly tackle-zone cells.
      const dist = Math.abs(nx - start[0]) + Math.abs(ny - start[1]);
      if (nCost > bestCost || (nCost === bestCost && dist < bestDist)) {
        bestCost = nCost; bestX = nx; bestY = ny; bestDist = dist;
      }
    }
    if (bestX === -1) return []; // no predecessor found — reachable set is disconnected
    path.unshift([cx, cy]);
    cx = bestX; cy = bestY; currentCost = bestCost;
  }

  return path; // excludes start, includes goal
}

// ─── Activation context (for hover card previews) ────────────────────────────

let activeAttackerId: string | null = null;
let activeTackleTargets: Set<string> = new Set();   // player IDs
let activeThrowerId: string | null = null;
let activeThrowTargetKeys: Set<string> = new Set(); // cell keys "x,y"
let activeLegalActions: LegalActions | null = null;

function clearActionHighlights(renderState: RenderState): void {
  renderState.moveRange = new Set();
  renderState.tackleHighlights = new Set();
  renderState.throwHighlights = new Set();
  renderState.highlights = new Set();
  renderState.movePath = [];
  renderState.movePathOrigin = null;
  renderState.previewLine = null;
  activeAttackerId = null;
  activeTackleTargets = new Set();
  activeThrowerId = null;
  activeThrowTargetKeys = new Set();
  activeLegalActions = null;
}

// ─── Error panel ──────────────────────────────────────────────────────────────

function showPromptError(cause: string, raw: unknown): void {
  const wrap = el("div", { padding: "20px" });

  const title = document.createElement("h2");
  title.textContent = "⚠ PROMPT DATA ERROR";
  Object.assign(title.style, {
    margin: "0 0 8px 0",
    fontSize: "15px",
    color: colors.danger,
    letterSpacing: "1px",
  });
  wrap.appendChild(title);

  const causeEl = document.createElement("p");
  causeEl.textContent = cause;
  Object.assign(causeEl.style, { margin: "0 0 12px 0", fontSize: "12px", color: colors.danger });
  wrap.appendChild(causeEl);

  const note = document.createElement("p");
  note.textContent = "The server sent a CHOOSE_ACTION prompt with missing or malformed data. Waiting for a valid prompt — no action can be taken.";
  Object.assign(note.style, { margin: "0", fontSize: "11px", color: colors.muted, fontStyle: "italic" });
  wrap.appendChild(note);

  console.error("[promptPanel] Malformed ActionPromptData:", cause, raw);
  setContent(wrap);
}

// ─── CHOOSE_ACTION ────────────────────────────────────────────────────────────

async function showChooseAction(
  prompt: ActionPromptMessage,
  ctx: PromptContext,
): Promise<InboundMessage | null> {
  const { state, renderState } = ctx;

  const parseResult = parseActionPromptData(prompt.data);
  if (!parseResult.ok) {
    showPromptError(parseResult.error, prompt.data);
    return null;
  }

  const data = parseResult.data;

  if (data.playerId) {
    const legalActions = data.legalActionsByPlayer[data.playerId];
    if (!legalActions) {
      showPromptError(
        `legalActionsByPlayer has no entry for active player "${data.playerId}"`,
        prompt.data,
      );
      return null;
    }
    return showActivatedPlayer(data.playerId, legalActions, state, renderState);
  }

  // AWAITING_ACTIVATION: pick from eligible players then show their actions
  const eligible = data.eligiblePlayerIds;

  const pickAndShow = async (): Promise<InboundMessage | null> => {
    renderState.highlights = new Set(eligible);
    const pid = await pickEligiblePlayer(eligible, ctx);
    renderState.highlights = new Set();

    const legalActions = data.legalActionsByPlayer[pid];
    if (!legalActions) {
      showPromptError(
        `legalActionsByPlayer has no entry for selected player "${pid}"`,
        prompt.data,
      );
      return null;
    }

    const onDeselect = () => {
      clearActionHighlights(renderState);
      pickAndShow().then((msg) => {
        if (msg) outerResolve(msg);
        else res(null); // error shown by pickAndShow; settle the outer promise
      });
    };
    return showActivatedPlayer(pid, legalActions, state, renderState, onDeselect);
  };

  let outerResolve!: (msg: InboundMessage) => void;
  return new Promise<InboundMessage | null>((res) => {
    outerResolve = (msg: InboundMessage) => res(msg);
    pickAndShow().then((msg) => { if (msg) res(msg); });
  });
}

function pickEligiblePlayer(
  eligible: string[],
  ctx: PromptContext,
): Promise<string> {
  return new Promise((resolve) => {
    const { state, renderState } = ctx;
    const wrap = el("div", { padding: "16px 20px" });
    wrap.appendChild(actionTitle("Activate a Player"));
    wrap.appendChild(subtext("Click an eligible player on the field or in the list."));

    const list = el("div", { display: "flex", flexDirection: "column", gap: "6px" });
    eligible.forEach((pid) => {
      const r = el("div", {
        padding: "8px 12px",
        border: `1px solid ${colors.border}`,
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "13px",
        color: colors.text,
      });
      r.textContent = state.getName(pid);
      r.addEventListener("mouseenter", () => { r.style.borderColor = colors.accent; });
      r.addEventListener("mouseleave", () => { r.style.borderColor = colors.border; });
      r.addEventListener("click", () => { renderState.fieldClickHandler = null; resolve(pid); });
      list.appendChild(r);
    });
    wrap.appendChild(list);
    setContent(wrap);

    renderState.fieldClickHandler = (gx: number, gy: number) => {
      for (const pid of eligible) {
        const pos = state.playerPositions.get(pid);
        if (pos && pos[0] === gx && pos[1] === gy) {
          renderState.fieldClickHandler = null;
          resolve(pid);
          return;
        }
      }
    };
  });
}

/** Main activation view: shows highlights on the field and handles click routing. */
function showActivatedPlayer(
  playerId: string,
  legalActions: LegalActions,
  state: LocalGameState,
  renderState: RenderState,
  onDeselect?: () => void,
): Promise<InboundMessage> {
  return new Promise((resolve) => {
    const isCarrier = state.ballCarrierId === playerId;
    const origin = state.playerPositions.get(playerId) ?? null;

    // Derive render sets from feed
    const moveRange  = new Set(legalActions.move.map((t) => `${t.x},${t.y}`));
    const tacklePids = new Set(legalActions.tackle.map((t) => t.targetId));
    const throwKeys  = new Set(legalActions.throw.map((t) => `${t.x},${t.y}`));

    renderState.moveRange        = moveRange;
    renderState.tackleHighlights = tacklePids;
    renderState.throwHighlights  = new Set(); // only shown after coach enters throw mode explicitly

    activeLegalActions     = legalActions;
    activeAttackerId       = playerId;
    activeTackleTargets    = tacklePids;
    activeThrowerId        = isCarrier ? playerId : null;
    activeThrowTargetKeys  = throwKeys;

    function done(msg: InboundMessage): void {
      clearActionHighlights(renderState);
      renderState.fieldClickHandler = null;
      resolve(msg);
    }

    // Build side panel
    const wrap = el("div", { padding: "16px 20px", display: "flex", flexDirection: "column", gap: "8px" });
    wrap.appendChild(actionTitle(state.getName(playerId)));

    const status = state.getPlayerStatus(playerId);
    let statusText = "";
    switch (status) {
      case "NORMAL":       statusText = "Ready"; break;
      case "PRONE":        statusText = "Down"; break;
      case "STUNNED":      statusText = "Stunned"; break;
      case "KNOCKED_OUT":  statusText = "Knocked Out"; break;
      case "INCAPACITATED":statusText = "Incapacitated"; break;
      default:             statusText = status;
    }
    wrap.appendChild(subtext(`${statusText}${isCarrier ? " · ball carrier" : ""}`));

    const playerInfo = state.players.get(playerId);
    const movTotal = playerInfo?.mov ?? 0;
    const movRemaining = state.movementRemaining.get(playerId) ?? movTotal;
    if (movTotal > 0) {
      const movRow = el("div", { display: "flex", alignItems: "center", gap: "5px", margin: "2px 0 0 0" });
      const movLabel = document.createElement("span");
      Object.assign(movLabel.style, { fontSize: "11px", color: colors.muted, width: "28px", flexShrink: "0" });
      movLabel.textContent = "MOV";
      movRow.appendChild(movLabel);
      for (let i = 0; i < movTotal; i++) {
        const pip = el("div", {
          width: "9px", height: "9px", borderRadius: "2px", flexShrink: "0",
          background: i < movRemaining ? "rgb(100,190,255)" : "rgba(255,255,255,0.15)",
        });
        movRow.appendChild(pip);
      }
      const movCount = document.createElement("span");
      Object.assign(movCount.style, { fontSize: "11px", color: colors.muted, marginLeft: "2px" });
      movCount.textContent = `${movRemaining}/${movTotal}`;
      movRow.appendChild(movCount);
      wrap.appendChild(movRow);
    }

    const legend = el("div", { display: "flex", flexDirection: "column", gap: "4px", margin: "4px 0 8px 0" });
    function legendItem(color: string, text: string): void {
      const row = el("div", { display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: colors.muted });
      const dot = el("div", { width: "10px", height: "10px", borderRadius: "2px", background: color, flexShrink: "0" });
      const lbl2 = document.createElement("span");
      lbl2.textContent = text;
      row.appendChild(dot);
      row.appendChild(lbl2);
      legend.appendChild(row);
    }
    if (moveRange.size > 0)  legendItem("rgb(30,80,160)", "Move range");
    if (tacklePids.size > 0) legendItem("rgb(220,60,60)", "Tackle target");
    if (throwKeys.size > 0)  legendItem("rgb(60,200,100)", "Throw / Handoff target");
    wrap.appendChild(legend);

    function makeBtn(text: string, handler: () => void, variant: "primary" | "secondary" | "ghost" = "secondary"): void {
      const b = btn(text, variant);
      b.style.width = "100%";
      b.style.textAlign = "left";
      b.addEventListener("click", handler);
      wrap.appendChild(b);
    }

    if (onDeselect) {
      makeBtn("← Deselect", () => { clearActionHighlights(renderState); renderState.fieldClickHandler = null; onDeselect(); }, "ghost");
    }
    if (legalActions.canStandUp) {
      makeBtn("Stand Up", () => done({ type: "STAND_UP_ACTION", playerId } as StandUpAction));
    }
    if (throwKeys.size > 0) {
      makeBtn("Throw / Handoff", () => enterThrowMode(playerId, legalActions, state, renderState, done, onDeselect));
    }
    if (legalActions.canTakeKnee) {
      makeBtn("Take a Knee", () => done({ type: "TAKE_KNEE", playerId } as TakeKnee), "ghost");
    }
    if (legalActions.canEndActivation) {
      makeBtn("End Activation", () => done({ type: "END_ACTIVATION", playerId } as EndActivation), "ghost");
    }

    // Auto-end when nothing is possible and the player can't be deselected
    const hasValidActions =
      moveRange.size > 0 ||
      tacklePids.size > 0 ||
      throwKeys.size > 0 ||
      legalActions.canStandUp ||
      legalActions.canTakeKnee ||
      legalActions.canEndActivation;
    if (!hasValidActions && !onDeselect) {
      const autoEndMsg = document.createElement("p");
      autoEndMsg.textContent = "No valid actions available - auto-ending activation...";
      Object.assign(autoEndMsg.style, {
        color: colors.muted,
        fontSize: "12px",
        fontStyle: "italic",
        marginTop: "8px",
        marginBottom: "4px",
      });
      wrap.appendChild(autoEndMsg);
      setContent(wrap);
      setTimeout(() => {
        done({ type: "END_ACTIVATION", playerId } as EndActivation);
      }, 600);
      return;
    }

    setContent(wrap);

    // Field click routing
    renderState.fieldClickHandler = (gx: number, gy: number) => {
      // Tackle target clicked
      for (const eid of tacklePids) {
        const epos = state.playerPositions.get(eid);
        if (epos && epos[0] === gx && epos[1] === gy) {
          const tackleTarget = legalActions.tackle.find((t) => t.targetId === eid);
          if (!tackleTarget) return;
          const apos = state.playerPositions.get(playerId);
          renderState.highlights = new Set([playerId]);
          renderState.tackleHighlights = new Set([eid]);
          renderState.throwHighlights = new Set();
          renderState.moveRange = new Set();
          if (apos) renderState.previewLine = { from: [apos[0], apos[1]], to: [epos[0], epos[1]] };
          renderState.fieldClickHandler = null;
          showTacklePreview(playerId, eid, tackleTarget.odds, state, renderState, done, () => {
            renderState.previewLine = null;
            showActivatedPlayer(playerId, legalActions, state, renderState, onDeselect).then(resolve);
          });
          return;
        }
      }

      // Move range cell clicked
      if (moveRange.has(`${gx},${gy}`) && origin) {
        const path = findPath(origin, [gx, gy], legalActions.move);
        if (path.length === 0) {
          console.error(`[promptPanel] findPath: no path to (${gx},${gy}) from (${origin[0]},${origin[1]})`);
          return; // stay in current state; user can pick a different cell
        }
        renderState.movePath = path;
        renderState.movePathOrigin = origin;
        renderState.fieldClickHandler = null;
        showMoveConfirm(playerId, path, renderState, done, () =>
          showActivatedPlayer(playerId, legalActions, state, renderState, onDeselect).then(resolve));
        return;
      }
    };
  });
}

// ─── Shared odds-bar helper ───────────────────────────────────────────────────

function oddsBar(pct: number, color: string, label: string, barW = 70, labelW = 32): HTMLDivElement {
  const row = el("div", { display: "flex", alignItems: "center", gap: "8px" });
  const bar = el("div", { width: `${barW}px`, height: "7px", background: "rgba(255,255,255,0.08)", borderRadius: "3px", flexShrink: "0" });
  const fill = el("div", { width: `${pct}%`, height: "100%", background: color, borderRadius: "3px" });
  bar.appendChild(fill);
  const pctLbl = document.createElement("span");
  Object.assign(pctLbl.style, { fontSize: "12px", fontWeight: "bold", color, width: `${labelW}px`, textAlign: "right", flexShrink: "0" });
  pctLbl.textContent = `${pct}%`;
  const nameLbl = document.createElement("span");
  Object.assign(nameLbl.style, { fontSize: "12px", color: colors.muted });
  nameLbl.textContent = label;
  row.appendChild(bar);
  row.appendChild(pctLbl);
  row.appendChild(nameLbl);
  return row;
}

// ─── Tackle preview ───────────────────────────────────────────────────────────

const TACKLE_OUTCOME_COLORS: Record<string, string> = {
  "Attacker Falls":    "rgb(200, 50, 50)",
  "Attacker Stumbles": "rgb(200, 110, 40)",
  "Lock Up":           "rgb(180, 165, 50)",
  "Defender Stumbles": "rgb(80, 190, 90)",
  "Defender Falls":    "rgb(50, 210, 80)",
};

function renderTackleOddsBars(odds: TackleOdds, container: HTMLElement): void {
  const outcomes: { label: string; pct: number }[] = [
    { label: "Attacker Falls",    pct: odds.attackerFallsPercent },
    { label: "Attacker Stumbles", pct: odds.attackerStumblesPercent },
    { label: "Lock Up",           pct: odds.lockUpPercent },
    { label: "Defender Stumbles", pct: odds.defenderStumblesPercent },
    { label: "Defender Falls",    pct: odds.defenderFallsPercent },
  ];
  const table = el("div", { display: "flex", flexDirection: "column", gap: "5px" });
  for (const { label, pct } of outcomes) {
    if (pct > 0) table.appendChild(oddsBar(pct, TACKLE_OUTCOME_COLORS[label] ?? colors.text, label));
  }
  container.appendChild(table);
}

function showTacklePreview(
  attackerId: string,
  defenderId: string,
  odds: TackleOdds,
  state: LocalGameState,
  renderState: RenderState,
  done: (msg: InboundMessage) => void,
  back: () => void,
): void {
  const wrap = el("div", { padding: "14px 20px", display: "flex", flexDirection: "column", gap: "8px" });
  wrap.appendChild(actionTitle(`Tackle: ${state.getName(attackerId)} → ${state.getName(defenderId)}`));

  renderTackleOddsBars(odds, wrap);

  const btnRow = el("div", { display: "flex", gap: "8px", marginTop: "4px" });
  const confirmBtn = btn("Confirm Tackle", "primary");
  confirmBtn.style.flex = "1";
  confirmBtn.addEventListener("click", () => {
    renderState.previewLine = null;
    done({ type: "TACKLE_ACTION", attackerId, defenderId } as TackleAction);
  });
  const cancelBtn = btn("Cancel", "ghost");
  cancelBtn.style.flex = "1";
  cancelBtn.addEventListener("click", back);
  btnRow.appendChild(confirmBtn);
  btnRow.appendChild(cancelBtn);
  wrap.appendChild(btnRow);

  setContent(wrap);
}

// ─── Throw / handoff preview ──────────────────────────────────────────────────

function showThrowPreview(
  throwerId: string,
  target: ThrowTarget,
  state: LocalGameState,
  renderState: RenderState,
  done: (msg: InboundMessage) => void,
  back: () => void,
): void {
  // Find player at target position for a descriptive title
  let receiverName: string | null = null;
  for (const [pid, pos] of state.playerPositions) {
    if (pos[0] === target.x && pos[1] === target.y) {
      receiverName = state.getName(pid);
      break;
    }
  }

  const wrap = el("div", { padding: "14px 20px", display: "flex", flexDirection: "column", gap: "8px" });
  const titleText = receiverName
    ? `Throw: ${state.getName(throwerId)} → ${receiverName}`
    : `Throw: ${state.getName(throwerId)} → (${target.x}, ${target.y})`;
  wrap.appendChild(actionTitle(titleText));

  const passColor = "rgb(80, 170, 255)";
  const catchColor = "rgb(80, 210, 100)";

  wrap.appendChild(oddsBar(target.passChancePercent, passColor, "Pass lands here"));
  if (target.catchChancePercent !== null) {
    wrap.appendChild(oddsBar(target.catchChancePercent, catchColor, "Caught if on target"));
  }

  const btnRow = el("div", { display: "flex", gap: "8px", marginTop: "4px" });
  const confirmBtn = btn("Confirm Throw", "primary");
  confirmBtn.style.flex = "1";
  confirmBtn.addEventListener("click", () => {
    renderState.previewLine = null;
    done({ type: "THROW_ACTION", throwerId, x: target.x, y: target.y } as ThrowAction);
  });
  const cancelBtn = btn("Cancel", "ghost");
  cancelBtn.style.flex = "1";
  cancelBtn.addEventListener("click", back);
  btnRow.appendChild(confirmBtn);
  btnRow.appendChild(cancelBtn);
  wrap.appendChild(btnRow);

  setContent(wrap);
}

// ─── Move confirm ─────────────────────────────────────────────────────────────

function showMoveConfirm(
  playerId: string,
  path: Array<[number, number]>,
  renderState: RenderState,
  done: (msg: InboundMessage) => void,
  back: () => void,
): void {
  const wrap = el("div", { padding: "16px 20px", display: "flex", flexDirection: "column", gap: "8px" });
  wrap.appendChild(actionTitle("Confirm Move"));

  const info = el("p", { fontSize: "12px", color: colors.muted, margin: "0" });
  const [tx, ty] = path[path.length - 1];
  info.textContent = `${path.length} step${path.length !== 1 ? "s" : ""} → (${tx}, ${ty})`;
  wrap.appendChild(info);

  const confirmBtn = btn("Confirm", "primary");
  confirmBtn.style.width = "100%";
  confirmBtn.addEventListener("click", () => {
    renderState.movePath = [];
    renderState.movePathOrigin = null;
    done({ type: "MOVE_ACTION", playerId, steps: path.map(([x, y]) => ({ x, y })) } as MoveAction);
  });

  const backBtn = btn("← Back", "ghost");
  backBtn.style.width = "100%";
  backBtn.addEventListener("click", () => {
    renderState.movePath = [];
    renderState.movePathOrigin = null;
    back();
  });

  wrap.appendChild(confirmBtn);
  wrap.appendChild(backBtn);
  setContent(wrap);
}

function enterThrowMode(
  playerId: string,
  legalActions: LegalActions,
  state: LocalGameState,
  renderState: RenderState,
  done: (msg: InboundMessage) => void,
  onDeselect?: () => void,
): void {
  // Show throw targets; hide move range while in throw mode.
  renderState.throwHighlights = new Set(legalActions.throw.map((t) => `${t.x},${t.y}`));
  renderState.moveRange = new Set();

  const wrap = el("div", { padding: "16px 20px" });
  wrap.appendChild(actionTitle("Throw / Handoff"));
  wrap.appendChild(subtext("Click a highlighted target on the field."));

  const backBtn = btn("← Back", "ghost");
  backBtn.addEventListener("click", () => {
    // showActivatedPlayer will restore move range and clear throw highlights.
    showActivatedPlayer(playerId, legalActions, state, renderState, onDeselect).then(done);
  });
  wrap.appendChild(backBtn);
  setContent(wrap);

  renderState.fieldClickHandler = (gx: number, gy: number) => {
    const target = legalActions.throw.find((t) => t.x === gx && t.y === gy);
    if (!target) return; // click outside fed throw targets ignored
    const tpos = state.playerPositions.get(playerId);
    if (tpos) renderState.previewLine = { from: [tpos[0], tpos[1]], to: [gx, gy] };
    renderState.fieldClickHandler = null;
    showThrowPreview(playerId, target, state, renderState, done, () => {
      renderState.previewLine = null;
      showActivatedPlayer(playerId, legalActions, state, renderState, onDeselect).then(done);
    });
  };
}

// ─── Main dispatcher ──────────────────────────────────────────────────────────

export async function showPrompt(
  prompt: ActionPromptMessage,
  ctx: PromptContext,
): Promise<InboundMessage | null> {
  switch (prompt.action) {
    case "CHOOSE_OFFENSE_DEFENSE":
      return showOffenseDefense();
    case "SUBMIT_FORMATION":
      return showFormation(prompt, ctx);
    case "CHOOSE_ACTION":
      return showChooseAction(prompt, ctx);
    default:
      console.warn(`[promptPanel] Unknown prompt: ${prompt.action}`);
      return null;
  }
}

// ─── Event log / idle view ────────────────────────────────────────────────────

export function renderIdlePanel(_state: LocalGameState): void {
  clearPanel();
}

// ─── Hover card ───────────────────────────────────────────────────────────────

export function renderHoverCard(state: LocalGameState, playerId: string): void {
  if (!panel || !hoverOverlayEl) return;
  const info = state.players.get(playerId);
  if (!info) return;

  hoverOverlayEl.innerHTML = "";
  hoverOverlayEl.style.display = "flex";

  const card = hoverOverlayEl;

  const name = document.createElement("p");
  Object.assign(name.style, { margin: "0 0 4px 0", fontSize: "18px", fontWeight: "bold", color: colors.gold });
  name.textContent = state.getName(playerId);
  card.appendChild(name);

  const roleRaw = info.role;
  const roleName = roleRaw.includes("_") ? roleRaw.split("_").slice(1).join(" ") : roleRaw;
  const roleLine = document.createElement("p");
  Object.assign(roleLine.style, { margin: "0 0 8px 0", fontSize: "12px", color: colors.muted, letterSpacing: "1px", textTransform: "uppercase" });
  roleLine.textContent = roleName;
  card.appendChild(roleLine);

  // Status badge
  const playerStatus = state.getPlayerStatus(playerId);
  if (playerStatus !== "NORMAL") {
    const statusConfig: Record<string, { label: string; color: string }> = {
      PRONE:        { label: "DOWNED",      color: "#ffd732" },
      STUNNED:      { label: "STUNNED",     color: "#ff8c00" },
      KNOCKED_OUT:  { label: "KNOCKED OUT", color: "#d22d2d" },
      INCAPACITATED:{ label: "OUT",         color: "#a000b4" },
    };
    const cfg = statusConfig[playerStatus];
    if (cfg) {
      const badge = el("div", { display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "10px" });
      const dot = el("div", { width: "8px", height: "8px", borderRadius: "50%", background: cfg.color, flexShrink: "0" });
      const lbl = document.createElement("span");
      Object.assign(lbl.style, { fontSize: "11px", fontWeight: "bold", color: cfg.color, letterSpacing: "1.5px" });
      const injury = state.playerInjuries.get(playerId);
      lbl.textContent = playerStatus === "INCAPACITATED" && injury
        ? `${cfg.label}: ${injury.replace(/_/g, " ")}`
        : cfg.label;
      badge.appendChild(dot);
      badge.appendChild(lbl);
      card.appendChild(badge);
    }
  }

  const statsRow = el("div", { display: "flex", gap: "20px", marginBottom: "10px" });
  const sign = (n: number) => n >= 0 ? `+${n}` : `${n}`;
  for (const [label2, value] of [["MOV", String(info.mov)], ["STR", sign(info.str)], ["DEX", sign(info.dex)]]) {
    const stat = el("div", { display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" });
    const val = document.createElement("span");
    Object.assign(val.style, { fontSize: "20px", fontWeight: "bold", color: colors.text });
    val.textContent = value;
    const lbl3 = document.createElement("span");
    Object.assign(lbl3.style, { fontSize: "11px", color: colors.muted, letterSpacing: "1px" });
    lbl3.textContent = label2;
    stat.appendChild(val);
    stat.appendChild(lbl3);
    statsRow.appendChild(stat);
  }
  card.appendChild(statsRow);

  if (info.skills.length > 0) {
    const skillsRow = el("div", { display: "flex", flexWrap: "wrap", gap: "8px" });
    for (const skill of info.skills) {
      const chip = el("div", {
        padding: "4px 10px",
        borderRadius: "3px",
        border: `1px solid ${colors.accent}`,
        fontSize: "12px",
        color: colors.accent,
        letterSpacing: "0.5px",
      });
      chip.textContent = skill.replace(/_/g, " ");
      skillsRow.appendChild(chip);
    }
    card.appendChild(skillsRow);
  }

  // Tackle odds — from feed, if this player is a tackle target in the current activation
  if (activeAttackerId && activeTackleTargets.has(playerId)) {
    const tackleTarget = activeLegalActions?.tackle.find((t) => t.targetId === playerId);
    if (tackleTarget) {
      card.appendChild(el("div", { borderTop: `1px solid ${colors.border}`, margin: "14px 0 10px 0" }));
      const tackleHeader = document.createElement("p");
      Object.assign(tackleHeader.style, { margin: "0 0 6px 0", fontSize: "11px", letterSpacing: "2px", textTransform: "uppercase", color: colors.muted });
      tackleHeader.textContent = "Tackle Odds";
      card.appendChild(tackleHeader);

      const oddsTable = el("div", { display: "flex", flexDirection: "column", gap: "6px" });
      const outcomes: { label: string; pct: number }[] = [
        { label: "Attacker Falls",    pct: tackleTarget.odds.attackerFallsPercent },
        { label: "Attacker Stumbles", pct: tackleTarget.odds.attackerStumblesPercent },
        { label: "Lock Up",           pct: tackleTarget.odds.lockUpPercent },
        { label: "Defender Stumbles", pct: tackleTarget.odds.defenderStumblesPercent },
        { label: "Defender Falls",    pct: tackleTarget.odds.defenderFallsPercent },
      ];
      for (const { label, pct } of outcomes) {
        if (pct > 0) oddsTable.appendChild(oddsBar(pct, TACKLE_OUTCOME_COLORS[label] ?? colors.text, label, 80, 34));
      }
      card.appendChild(oddsTable);
    }
  }

  // Throw odds — from feed, if this player's position is a throw target in the current activation
  const pos = state.playerPositions.get(playerId);
  if (activeThrowerId && pos && activeThrowTargetKeys.has(`${pos[0]},${pos[1]}`)) {
    const throwTarget = activeLegalActions?.throw.find((t) => t.x === pos[0] && t.y === pos[1]);
    if (throwTarget) {
      card.appendChild(el("div", { borderTop: `1px solid ${colors.border}`, margin: "14px 0 10px 0" }));
      const throwHeader = document.createElement("p");
      Object.assign(throwHeader.style, { margin: "0 0 6px 0", fontSize: "11px", letterSpacing: "2px", textTransform: "uppercase", color: colors.muted });
      throwHeader.textContent = "Throw";
      card.appendChild(throwHeader);

      const throwTable = el("div", { display: "flex", flexDirection: "column", gap: "6px" });
      throwTable.appendChild(oddsBar(throwTarget.passChancePercent, "rgb(80, 170, 255)", "Pass lands here", 80, 34));
      if (throwTarget.catchChancePercent !== null) {
        throwTable.appendChild(oddsBar(throwTarget.catchChancePercent, "rgb(80, 210, 100)", "Caught if on target", 80, 34));
      }
      card.appendChild(throwTable);
    }
  }
}

export function clearHoverCard(): void {
  if (!hoverOverlayEl) return;
  hoverOverlayEl.innerHTML = "";
  hoverOverlayEl.style.display = "none";
}
